import * as THREE from "./vendor/three.module.js";

const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
const smooth = (value) => {
  const v = clamp(value);
  return v * v * (3 - 2 * v);
};
const mix = (a, b, amount) => a + (b - a) * amount;
const windowFade = (p, start, inEnd, outStart, end) =>
  smooth((p - start) / Math.max(.0001, inEnd - start)) *
  (1 - smooth((p - outStart) / Math.max(.0001, end - outStart)));

export function mountSectorWorld(container, options = {}) {
  const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const mobile = matchMedia("(max-width: 760px)").matches;
  let resolveReady;
  let readyResolved = false;
  const ready = new Promise(resolve => { resolveReady = resolve; });
  const renderer = new THREE.WebGLRenderer({
    antialias: false,
    alpha: false,
    powerPreference: "high-performance",
  });
  const areaRatio = Math.sqrt(1050000 / Math.max(1, innerWidth * innerHeight));
  let pixelRatio = Math.min(devicePixelRatio || 1, mobile ? 1.08 : 1.5, Math.max(.72, areaRatio));
  renderer.setPixelRatio(pixelRatio);
  renderer.setClearColor(0x050505, 1);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.12;
  const canvas = renderer.domElement;
  canvas.setAttribute("aria-hidden", "true");
  container.appendChild(canvas);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x050505);
  scene.fog = new THREE.FogExp2(0x050505, mobile ? .012 : .009);
  const camera = new THREE.PerspectiveCamera(mobile ? 66 : 56, 1, .1, 900);
  scene.add(camera);

  const ambient = new THREE.AmbientLight(0xffffff, .2);
  const key = new THREE.DirectionalLight(0xffffff, 2.8);
  key.position.set(-12, 18, 16);
  const rim = new THREE.DirectionalLight(0xffffff, 1.45);
  rim.position.set(16, 3, -24);
  const heartLight = new THREE.PointLight(0xffffff, 5, 95, 2);
  heartLight.position.set(0, 8, -210);
  scene.add(ambient, key, rim, heartLight);
  const passageLight = new THREE.PointLight(0xffffff, 0, 42, 1.7);
  passageLight.position.set(0, 0, 2);
  camera.add(passageLight);

  const lineMaterials = [];
  const lineMaterial = (opacity = 1, color = 0xf1f1ed) => {
    const material = new THREE.LineBasicMaterial({color, transparent: true, opacity, depthWrite: false});
    lineMaterials.push(material);
    return material;
  };
  const solidMaterial = new THREE.MeshStandardMaterial({
    color: 0x090909,
    roughness: .72,
    metalness: .06,
    transparent: true,
    opacity: .94,
  });
  const paleMaterial = new THREE.MeshStandardMaterial({
    color: 0xe9e9e4,
    emissive: 0xffffff,
    emissiveIntensity: .14,
    roughness: .52,
    metalness: .02,
    transparent: true,
    opacity: 1,
  });
  const surfaceMaterials = [];

  function appendSegment(target, a, b) {
    target.push(a.x, a.y, a.z, b.x, b.y, b.z);
  }
  function lineSegments(points, material) {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(points, 3));
    return new THREE.LineSegments(geometry, material);
  }
  function appendRing(target, center, radius, sides, plane = "xz", rotation = 0) {
    const points = [];
    for (let index = 0; index < sides; index += 1) {
      const angle = rotation + index / sides * Math.PI * 2;
      points.push(plane === "xz"
        ? new THREE.Vector3(center.x + Math.cos(angle) * radius, center.y, center.z + Math.sin(angle) * radius)
        : new THREE.Vector3(center.x + Math.cos(angle) * radius, center.y + Math.sin(angle) * radius, center.z));
    }
    points.forEach((point, index) => appendSegment(target, point, points[(index + 1) % points.length]));
    return points;
  }
  function edgedMesh(geometry, material, edgeOpacity = .55) {
    const group = new THREE.Group();
    const meshMaterial = material.clone();
    meshMaterial.userData.signalRole = material === paleMaterial ? "pale" : "solid";
    surfaceMaterials.push(meshMaterial);
    const mesh = new THREE.Mesh(geometry, meshMaterial);
    const edges = new THREE.LineSegments(new THREE.EdgesGeometry(geometry, 18), lineMaterial(edgeOpacity));
    group.add(mesh, edges);
    group.userData.mesh = mesh;
    group.userData.edges = edges;
    return group;
  }
  function monolithBodyGeometry() {
    const levels = [
      {y: -13, x: 3.35, z: 2.95},
      {y: -11.4, x: 3.02, z: 2.65},
      {y: 8.8, x: 2.28, z: 2.02},
      {y: 12.1, x: 3.05, z: 2.62},
      {y: 13, x: 3.05, z: 2.62},
    ];
    const vertices = [];
    levels.forEach(level => {
      vertices.push(
        -level.x, level.y, -level.z,
         level.x, level.y, -level.z,
         level.x, level.y,  level.z,
        -level.x, level.y,  level.z,
      );
    });
    const indices = [];
    indices.push(0, 2, 1, 0, 3, 2);
    for (let level = 0; level < levels.length - 1; level += 1) {
      const a = level * 4;
      const b = (level + 1) * 4;
      for (let side = 0; side < 4; side += 1) {
        const next = (side + 1) % 4;
        indices.push(a + side, a + next, b + next, a + side, b + next, b + side);
      }
    }
    const top = (levels.length - 1) * 4;
    indices.push(top, top + 1, top + 2, top, top + 2, top + 3);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    return geometry;
  }
  function glowSprite(size) {
    const textureCanvas = document.createElement("canvas");
    textureCanvas.width = textureCanvas.height = 128;
    const context = textureCanvas.getContext("2d");
    const gradient = context.createRadialGradient(64, 64, 0, 64, 64, 64);
    gradient.addColorStop(0, "rgba(255,255,255,.92)");
    gradient.addColorStop(.22, "rgba(255,255,255,.26)");
    gradient.addColorStop(1, "rgba(255,255,255,0)");
    context.fillStyle = gradient;
    context.fillRect(0, 0, 128, 128);
    const texture = new THREE.CanvasTexture(textureCanvas);
    const material = new THREE.SpriteMaterial({
      map: texture,
      color: 0xffffff,
      transparent: true,
      opacity: .16,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(size, size, 1);
    return sprite;
  }

  function crystalGeometry(profile = 0) {
    const waist = profile === 0 ? .58 : profile === 1 ? .72 : .46;
    const lean = profile === 0 ? .22 : profile === 1 ? -.3 : .4;
    const vertices = new Float32Array([
      -.62, -1, -.48,  .62, -1, -.48,  .62, -1, .48,  -.62, -1, .48,
      -waist, .22, -.34, waist, .22, -.34, waist, .22, .34, -waist, .22, .34,
      lean, 1.72, 0,
    ]);
    const indices = [
      0,2,1, 0,3,2,
      0,1,5, 0,5,4,
      1,2,6, 1,6,5,
      2,3,7, 2,7,6,
      3,0,4, 3,4,7,
      4,5,8, 5,6,8, 6,7,8, 7,4,8,
    ];
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    return geometry;
  }

  function drawSignalType(canvas, texture) {
    const context = canvas.getContext("2d");
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.font = "800 318px Syne, Arial Black, sans-serif";
    context.fillStyle = "rgba(241,241,237,.88)";
    context.fillText("RIZE UP", canvas.width / 2, canvas.height / 2 + 14);
    context.strokeStyle = "rgba(241,241,237,.18)";
    context.lineWidth = 4;
    context.strokeText("RIZE UP", canvas.width / 2, canvas.height / 2 + 14);
    texture.needsUpdate = true;
  }

  let seed = 0x52a17e;
  const random = () => {
    seed = Math.imul(seed ^ seed >>> 15, 1 | seed);
    seed ^= seed + Math.imul(seed ^ seed >>> 7, 61 | seed);
    return ((seed ^ seed >>> 14) >>> 0) / 4294967296;
  };

  // SIGNAL HOME
  // One authored crystalline organism surrounds the mark. On scroll the same
  // fragments mobilize into the entry passage instead of cutting to a new scene.
  const signalHome = new THREE.Group();
  signalHome.position.z = -2;
  scene.add(signalHome);

  const typeCanvas = document.createElement("canvas");
  typeCanvas.width = 2048;
  typeCanvas.height = 512;
  const typeTexture = new THREE.CanvasTexture(typeCanvas);
  typeTexture.colorSpace = THREE.SRGBColorSpace;
  drawSignalType(typeCanvas, typeTexture);
  document.fonts?.ready.then(() => drawSignalType(typeCanvas, typeTexture));
  const typeMaterial = new THREE.MeshBasicMaterial({
    map: typeTexture,
    transparent: true,
    opacity: .14,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
  });
  const signalType = new THREE.Mesh(new THREE.PlaneGeometry(mobile ? 45 : 62, mobile ? 11.25 : 15.5), typeMaterial);
  signalType.position.set(0, mobile ? 10 : 8, -16);
  signalHome.add(signalType);

  const crystalSurfaceMaterial = new THREE.MeshPhysicalMaterial({
    color: 0x090a0b,
    metalness: .82,
    roughness: .2,
    clearcoat: 1,
    clearcoatRoughness: .12,
    reflectivity: 1,
    transparent: true,
    opacity: .96,
    side: THREE.DoubleSide,
  });
  const crystalWireMaterial = new THREE.MeshBasicMaterial({
    color: 0xf1f1ed,
    wireframe: true,
    transparent: true,
    opacity: .34,
    depthWrite: false,
  });
  const crystalGeometries = [crystalGeometry(0), crystalGeometry(1), crystalGeometry(2)];
  const crystalMeshes = crystalGeometries.map(geometry => {
    const surface = new THREE.InstancedMesh(geometry, crystalSurfaceMaterial, mobile ? 34 : 54);
    const wire = new THREE.InstancedMesh(geometry, crystalWireMaterial, mobile ? 34 : 54);
    surface.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    wire.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    surface.frustumCulled = false;
    wire.frustumCulled = false;
    signalHome.add(surface, wire);
    return {surface, wire, count:0};
  });
  const homeShards = [];
  const shardTotal = mobile ? 34 : 54;
  for (let index = 0; index < shardTotal; index += 1) {
    const type = index % 3;
    const phase = index / shardTotal * Math.PI * 2;
    const heroShard = index < 6;
    const authoredPhase = heroShard ? index / 6 * Math.PI * 2 : phase;
    const jitter = (random() - .5) * .48;
    const radius = heroShard ? 12.2 + (index % 2) * 2.4 : 7.5 + random() * 10.5;
    const wing = index % 2 ? 1 : -1;
    const homeX = Math.cos(authoredPhase + (heroShard ? 0 : jitter)) * radius * (heroShard ? 1 : 1.05 + random() * .36);
    const homeY = 1.5 + Math.sin(authoredPhase + (heroShard ? 0 : jitter)) * radius * (heroShard ? .72 : .76)
      + (heroShard ? 0 : (1 - Math.abs(Math.cos(phase))) * 4.5);
    const homeZ = heroShard ? -5.5 - (index % 2) * 1.8 : -1.5 - random() * 9 - Math.abs(homeX) * .08;
    const tunnelRing = Math.floor(index / 8);
    const tunnelSide = index % 8;
    const tunnelAngle = tunnelSide / 8 * Math.PI * 2 + Math.PI / 8;
    const tunnelRadius = 6.1 + (index % 3) * .72;
    homeShards.push({
      type,
      slot: crystalMeshes[type].count++,
      homePosition: new THREE.Vector3(homeX, homeY, homeZ),
      homeRotation: new THREE.Euler(
        heroShard ? (index % 2 ? .16 : -.16) : (random() - .5) * .65,
        heroShard ? (index % 2 ? -.22 : .22) : (random() - .5) * .9,
        -authoredPhase + Math.PI / 2 + (heroShard ? 0 : jitter),
      ),
      homeScale: heroShard
        ? new THREE.Vector3(.82 + (index % 3) * .14, 7.8 + (index % 2) * 2.2, .76 + (index % 2) * .18)
        : new THREE.Vector3(.42 + random() * .88, 2.1 + random() * 4.8, .42 + random() * .92),
      tunnelPosition: new THREE.Vector3(
        Math.cos(tunnelAngle) * tunnelRadius,
        Math.sin(tunnelAngle) * tunnelRadius,
        4 - tunnelRing * 12.5 - (index % 2) * 3.5,
      ),
      tunnelRotation: new THREE.Euler(tunnelAngle + Math.PI / 2, 0, -tunnelAngle),
      tunnelScale: new THREE.Vector3(.34 + random() * .42, 2.8 + random() * 4.3, .4 + random() * .56),
      delay: random() * .22,
      wing,
    });
  }
  crystalMeshes.forEach(({surface, wire, count}) => {
    surface.count = count;
    wire.count = count;
  });

  const signalHalo = new THREE.Group();
  const haloMaterialA = lineMaterial(.72);
  const haloMaterialB = lineMaterial(.18);
  const haloA = [];
  const haloB = [];
  [6.6, 8.4, 11.2, 14.8].forEach((radius, index) =>
    appendRing(index < 2 ? haloA : haloB, new THREE.Vector3(0, 1.5, -4), radius, 64, "xy", index * .08));
  for (let index = 0; index < 12; index += 1) {
    const angle = index / 12 * Math.PI * 2;
    appendSegment(haloB,
      new THREE.Vector3(Math.cos(angle) * 6.6, 1.5 + Math.sin(angle) * 6.6, -4),
      new THREE.Vector3(Math.cos(angle) * 15.8, 1.5 + Math.sin(angle) * 15.8, -4));
  }
  signalHalo.add(lineSegments(haloA, haloMaterialA), lineSegments(haloB, haloMaterialB));
  signalHome.add(signalHalo);

  const homeLogo = new THREE.Group();
  const homeGlow = glowSprite(28);
  homeGlow.position.set(0, 1.5, -2);
  signalHome.add(homeGlow);

  const homeLight = new THREE.PointLight(0xffffff, 12, 72, 2);
  homeLight.position.set(0, 2, 8);
  signalHome.add(homeLight);

  const chamber = new THREE.Group();
  chamber.position.z = -210;
  scene.add(chamber);

  // Entry tunnel: one batched architectural shell that delivers the first frame.
  const tunnel = new THREE.Group();
  const tunnelBright = [];
  const tunnelDim = [];
  const sides = 8;
  const tunnelRadius = mobile ? 5.3 : 6.2;
  const tunnelLength = 178;
  const tunnelRings = 43;
  let previousRing = null;
  for (let index = 0; index < tunnelRings; index += 1) {
    const z = 12 - index * tunnelLength / (tunnelRings - 1);
    const radius = tunnelRadius * (1 + Math.sin(index * .43) * .018);
    const ring = appendRing(index % 5 === 0 ? tunnelBright : tunnelDim, new THREE.Vector3(0, 0, z), radius, sides, "xy", Math.PI / 8);
    if (previousRing) {
      ring.forEach((point, side) => appendSegment(side % 2 ? tunnelDim : tunnelBright, previousRing[side], point));
    }
    previousRing = ring;
  }
  const tunnelBrightMaterial = lineMaterial(.8);
  const tunnelDimMaterial = lineMaterial(.19);
  tunnel.add(lineSegments(tunnelBright, tunnelBrightMaterial), lineSegments(tunnelDim, tunnelDimMaterial));

  const tunnelShell = new THREE.Group();
  const shellDarkPositions = [];
  const shellPalePositions = [];
  const shellSegments = mobile ? 15 : 22;
  const shellSides = 8;
  const shellRadius = mobile ? 5.35 : 6.25;
  const shellPoint = (ring, side, inset = 0) => {
    const angle = side / shellSides * Math.PI * 2 + Math.PI / 8;
    const radius = shellRadius + Math.sin(ring * .72 + side * 1.9) * .36 - inset;
    return new THREE.Vector3(
      Math.cos(angle) * radius,
      Math.sin(angle) * radius,
      6 - ring * 178 / shellSegments,
    );
  };
  const pushTriangle = (target, a, b, c) => target.push(
    a.x,a.y,a.z, b.x,b.y,b.z, c.x,c.y,c.z,
  );
  for (let ring = 0; ring < shellSegments; ring += 1) {
    for (let side = 0; side < shellSides; side += 1) {
      const a = shellPoint(ring, side);
      const b = shellPoint(ring, side + 1);
      const c = shellPoint(ring + 1, side);
      const d = shellPoint(ring + 1, side + 1);
      const open = (ring * 5 + side * 3) % 11;
      if (open !== 0 && open !== 1) {
        pushTriangle(shellDarkPositions, a, b, ring % 2 ? c : d);
        if ((ring + side) % 3 !== 0) pushTriangle(shellDarkPositions, b, d, c);
      }
      if ((ring * 7 + side * 5) % 17 === 0) {
        const insetA = shellPoint(ring, side, .08);
        const insetB = shellPoint(ring, side + 1, .08);
        const insetD = shellPoint(ring + 1, side + 1, .08);
        pushTriangle(shellPalePositions, insetA, insetB, insetD);
      }
    }
  }
  const shellDarkGeometry = new THREE.BufferGeometry();
  shellDarkGeometry.setAttribute("position", new THREE.Float32BufferAttribute(shellDarkPositions, 3));
  shellDarkGeometry.computeVertexNormals();
  const shellDarkMaterial = new THREE.MeshPhysicalMaterial({
    color:0x111214,metalness:.92,roughness:.16,clearcoat:1,clearcoatRoughness:.1,
    side:THREE.DoubleSide,transparent:true,opacity:0,
  });
  const shellDark = new THREE.Mesh(shellDarkGeometry, shellDarkMaterial);
  const shellPaleGeometry = new THREE.BufferGeometry();
  shellPaleGeometry.setAttribute("position", new THREE.Float32BufferAttribute(shellPalePositions, 3));
  shellPaleGeometry.computeVertexNormals();
  const shellPaleMaterial = new THREE.MeshBasicMaterial({
    color:0xf1f1ed,side:THREE.DoubleSide,transparent:true,opacity:0,
    blending:THREE.AdditiveBlending,depthWrite:false,toneMapped:false,
  });
  const shellPale = new THREE.Mesh(shellPaleGeometry, shellPaleMaterial);
  tunnelShell.add(shellDark, shellPale);
  tunnel.add(tunnelShell);
  scene.add(tunnel);

  // The chamber floor is shared by every act after entry.
  const floorGroup = new THREE.Group();
  const floorBright = [];
  const floorDim = [];
  [6, 11, 18, 27, 39, 54].forEach((radius, index) =>
    appendRing(index < 3 ? floorBright : floorDim, new THREE.Vector3(0, -7.2, 0), radius, 48, "xz", index * .04));
  for (let index = 0; index < 24; index += 1) {
    const angle = index / 24 * Math.PI * 2;
    appendSegment(
      index % 3 === 0 ? floorBright : floorDim,
      new THREE.Vector3(Math.cos(angle) * 5.7, -7.2, Math.sin(angle) * 5.7),
      new THREE.Vector3(Math.cos(angle) * 55, -7.2, Math.sin(angle) * 55),
    );
  }
  const floorBrightMaterial = lineMaterial(.6);
  const floorDimMaterial = lineMaterial(.11);
  floorGroup.add(lineSegments(floorBright, floorBrightMaterial), lineSegments(floorDim, floorDimMaterial));
  chamber.add(floorGroup);

  // A low star field creates scale without additional draw calls.
  const dustPositions = [];
  for (let index = 0; index < (mobile ? 280 : 620); index += 1) {
    const radius = 18 + random() * 115;
    const angle = random() * Math.PI * 2;
    dustPositions.push(Math.cos(angle) * radius, -2 + (random() - .5) * 74, Math.sin(angle) * radius);
  }
  const dustGeometry = new THREE.BufferGeometry();
  dustGeometry.setAttribute("position", new THREE.Float32BufferAttribute(dustPositions, 3));
  const dustMaterial = new THREE.PointsMaterial({color: 0xffffff, size: .055, transparent: true, opacity: .34, depthWrite: false});
  const dust = new THREE.Points(dustGeometry, dustMaterial);
  chamber.add(dust);

  // SECTOR CAPITAL
  // ArchitectureVibe is treated as a grammar rather than a model reference:
  // nested portals, terraced slabs, ribbed chambers, one helix and intentional
  // black voids form a single vertical megastructure around the signal core.
  const sectorCapital = new THREE.Group();
  sectorCapital.position.set(0, 5.5, -2);
  chamber.add(sectorCapital);

  const capitalBrightMaterial = lineMaterial(0);
  const capitalDimMaterial = lineMaterial(0);
  const capitalBright = [];
  const capitalDim = [];

  // Nested portal frames establish one legible crown and keep the center open.
  for (let index = 0; index < 9; index += 1) {
    const width = 18 + index * 2.35;
    const height = 15 + index * 2.05;
    const z = -10 - index * .72;
    const y = 13.5 + index * .35;
    const left = -width / 2;
    const right = width / 2;
    const bottom = y - height / 2;
    const top = y + height / 2;
    const target = index < 4 ? capitalBright : capitalDim;
    appendSegment(target, new THREE.Vector3(left, bottom, z), new THREE.Vector3(left, top, z));
    appendSegment(target, new THREE.Vector3(left, top, z), new THREE.Vector3(right, top, z));
    appendSegment(target, new THREE.Vector3(right, top, z), new THREE.Vector3(right, bottom, z));
  }

  // One large ribbed chamber creates the architectural "lung" seen in the
  // reference while remaining open enough for the RiZe mark to own the axis.
  for (let index = 0; index < 15; index += 1) {
    const radius = 12.5 + index * .62;
    const z = -5.5 - index * .46;
    const start = new THREE.Vector3();
    let previous = null;
    for (let step = 0; step <= 18; step += 1) {
      const angle = mix(-Math.PI * .68, Math.PI * .68, step / 18);
      start.set(Math.cos(angle) * radius, 2.5 + Math.sin(angle) * radius, z);
      if (previous) appendSegment(index % 4 === 0 ? capitalBright : capitalDim, previous, start);
      previous = start.clone();
    }
  }

  // The helical route gives the eye one continuous path through the collage.
  let previousHelix = null;
  for (let index = 0; index <= 96; index += 1) {
    const amount = index / 96;
    const angle = amount * Math.PI * 7.5;
    const radius = mix(10.8, 6.2, amount);
    const point = new THREE.Vector3(
      Math.cos(angle) * radius + 12.5,
      -11 + amount * 33,
      Math.sin(angle) * radius - 2,
    );
    if (previousHelix) appendSegment(index % 8 < 2 ? capitalBright : capitalDim, previousHelix, point);
    previousHelix = point;
  }

  const portalLines = lineSegments(capitalBright, capitalBrightMaterial);
  const capitalStructure = lineSegments(capitalDim, capitalDimMaterial);
  sectorCapital.add(portalLines, capitalStructure);

  // Dark metallic slabs are organized in three terraces, not randomized across
  // the frame. Their silhouette supplies mass without becoming a generic skyline.
  const capitalSlabGeometry = new THREE.BoxGeometry(1, 1, 1);
  const capitalSlabMaterial = new THREE.MeshStandardMaterial({
    color: 0x0b0c0e,
    metalness: .84,
    roughness: .28,
    transparent: true,
    opacity: 0,
  });
  const capitalSlabs = new THREE.InstancedMesh(capitalSlabGeometry, capitalSlabMaterial, mobile ? 28 : 42);
  const capitalSlabWireMaterial = new THREE.MeshBasicMaterial({
    color: 0xe8e8e3,
    wireframe: true,
    transparent: true,
    opacity: 0,
    depthWrite: false,
  });
  const capitalSlabWires = new THREE.InstancedMesh(capitalSlabGeometry, capitalSlabWireMaterial, mobile ? 28 : 42);
  const capitalDummy = new THREE.Object3D();
  const capitalSlabCount = mobile ? 28 : 42;
  for (let index = 0; index < capitalSlabCount; index += 1) {
    const terrace = index % 3;
    const level = Math.floor(index / 3);
    const side = terrace === 1 ? 1 : -1;
    const width = terrace === 1 ? 10.5 : 8.5 + (level % 3) * 2.4;
    capitalDummy.position.set(
      side * (12 + terrace * 5.2 + (level % 2) * 1.4),
      -8 + level * 2.45,
      -3 - terrace * 5.5 + (level % 4) * 1.25,
    );
    capitalDummy.rotation.set(
      0,
      (terrace - 1) * .16 + (level % 2 ? .055 : -.055),
      side * (level % 3 - 1) * .018,
    );
    capitalDummy.scale.set(width, .48 + (level % 4) * .11, 2.1 + (level % 3) * .72);
    capitalDummy.updateMatrix();
    capitalSlabs.setMatrixAt(index, capitalDummy.matrix);
    capitalSlabWires.setMatrixAt(index, capitalDummy.matrix);
  }
  capitalSlabs.instanceMatrix.needsUpdate = true;
  capitalSlabWires.instanceMatrix.needsUpdate = true;
  sectorCapital.add(capitalSlabs, capitalSlabWires);

  // Sparse crystalline tissue connects the hard architecture. Large gaps are
  // intentional: they are the black voids that make the reference feel enormous.
  const tissueGeometry = crystalGeometry(2);
  const tissueMaterial = new THREE.MeshPhysicalMaterial({
    color: 0x111214,
    metalness: .9,
    roughness: .2,
    clearcoat: 1,
    clearcoatRoughness: .12,
    transparent: true,
    opacity: 0,
  });
  const tissueWireMaterial = new THREE.MeshBasicMaterial({
    color: 0xf1f1ed,
    wireframe: true,
    transparent: true,
    opacity: 0,
    depthWrite: false,
  });
  const tissueCount = mobile ? 18 : 30;
  const capitalTissue = new THREE.InstancedMesh(tissueGeometry, tissueMaterial, tissueCount);
  const capitalTissueWire = new THREE.InstancedMesh(tissueGeometry, tissueWireMaterial, tissueCount);
  for (let index = 0; index < tissueCount; index += 1) {
    const cluster = index % 3;
    const amount = Math.floor(index / 3) / Math.max(1, Math.ceil(tissueCount / 3) - 1);
    const side = cluster === 1 ? 1 : -1;
    capitalDummy.position.set(
      side * (20 + cluster * 3.2 + Math.sin(index * 2.4) * 4.5),
      -12 + amount * 43 + Math.cos(index * 1.7) * 3.2,
      -8 + cluster * 6 + Math.sin(index) * 3,
    );
    capitalDummy.rotation.set(index * .41, index * .73, index * .29);
    capitalDummy.scale.set(.55 + random() * .7, 2.4 + random() * 4.8, .5 + random() * .9);
    capitalDummy.updateMatrix();
    capitalTissue.setMatrixAt(index, capitalDummy.matrix);
    capitalTissueWire.setMatrixAt(index, capitalDummy.matrix);
  }
  capitalTissue.instanceMatrix.needsUpdate = true;
  capitalTissueWire.instanceMatrix.needsUpdate = true;
  sectorCapital.add(capitalTissue, capitalTissueWire);

  // The six monoliths are the persistent cast. Their formation changes with the story.
  const monoliths = [];
  const monolithGeometry = monolithBodyGeometry();
  for (let index = 0; index < 6; index += 1) {
    const monolith = edgedMesh(monolithGeometry, solidMaterial, index === 0 ? .92 : .48);
    const slotMaterial = new THREE.MeshBasicMaterial({color: 0xf1f1ed, transparent: true, opacity: .18});
    const slot = new THREE.Mesh(new THREE.PlaneGeometry(.16, 15), slotMaterial);
    slot.position.set(0, -.3, 2.27);
    monolith.add(slot);
    monolith.userData.slot = slot;
    monolith.userData.index = index;
    chamber.add(monolith);
    monoliths.push(monolith);
  }

  const formations = [
    {at:.14, radius:31, spread:1, height:.72, rotation:0},
    {at:.28, radius:26, spread:.9, height:.9, rotation:.16},
    {at:.43, radius:23, spread:1, height:1, rotation:.62},
    {at:.60, radius:26, spread:1.05, height:1.12, rotation:1.18},
    {at:.74, radius:18, spread:.72, height:.82, rotation:1.55},
    {at:.87, radius:30, spread:1.14, height:.94, rotation:2.18},
    {at:1, radius:14, spread:.6, height:1.2, rotation:Math.PI * 2},
  ];
  function formationAt(progress) {
    for (let index = 0; index < formations.length - 1; index += 1) {
      const start = formations[index];
      const end = formations[index + 1];
      if (progress <= end.at) {
        const amount = smooth((progress - start.at) / Math.max(.0001, end.at - start.at));
        return {
          radius: mix(start.radius, end.radius, amount),
          spread: mix(start.spread, end.spread, amount),
          height: mix(start.height, end.height, amount),
          rotation: mix(start.rotation, end.rotation, amount),
        };
      }
    }
    return formations.at(-1);
  }

  // Central dimensional RiZe mark.
  const logoPoints = [[530.335,84.784],[2469.67,84.784],[1917.18,637.153],[2195.3,915.216],[1804.43,915.216],[1563.89,674.729],[1116.64,674.729],[883.624,441.757],[1721.75,441.757],[1842.01,321.514],[763.355,321.514]];
  const logoShape = new THREE.Shape();
  logoPoints.forEach(([x, y], index) => {
    const px = (x - 1500) * .008;
    const py = (500 - y) * .008;
    if (index) logoShape.lineTo(px, py);
    else logoShape.moveTo(px, py);
  });
  logoShape.closePath();
  const logoGeometry = new THREE.ExtrudeGeometry(logoShape, {
    depth: 1.5,
    steps: 1,
    bevelEnabled: true,
    bevelThickness: .24,
    bevelSize: .15,
    bevelSegments: 3,
  });
  logoGeometry.center();
  const homeLogoObject = edgedMesh(logoGeometry, paleMaterial, 1);
  homeLogoObject.scale.setScalar(mobile ? .72 : .92);
  homeLogoObject.position.set(0, 1.5, -1);
  homeLogo.add(homeLogoObject);
  signalHome.add(homeLogo);
  const logoRoot = edgedMesh(logoGeometry, paleMaterial, .86);
  logoRoot.scale.setScalar(mobile ? .62 : .82);
  logoRoot.position.y = 1.8;
  chamber.add(logoRoot);
  const logoGlow = glowSprite(25);
  logoGlow.position.set(0, 1.8, -1);
  chamber.add(logoGlow);

  // Archive screen: real RiZe history held inside the same architecture.
  const archive = new THREE.Group();
  archive.position.set(mobile ? 0 : 12, 3, -3);
  const archiveTexture = new THREE.Texture();
  archiveTexture.colorSpace = THREE.SRGBColorSpace;
  const commitArchiveTexture = image => {
    archiveTexture.image = image;
    archiveTexture.needsUpdate = true;
    renderer.initTexture(archiveTexture);
  };
  if ("createImageBitmap" in window) {
    new THREE.ImageBitmapLoader()
      .setOptions({imageOrientation: "flipY"})
      .load("assets/images/rize-first-teamtage.jpg", commitArchiveTexture);
  } else {
    new THREE.ImageLoader().load("assets/images/rize-first-teamtage.jpg", commitArchiveTexture);
  }
  const archiveScreenMaterial = new THREE.MeshBasicMaterial({
    map: archiveTexture,
    color: 0xd7d7d2,
    transparent: true,
    opacity: 0,
    toneMapped: false,
    depthTest: false,
  });
  const archiveScreen = new THREE.Mesh(new THREE.PlaneGeometry(24, 13.5), archiveScreenMaterial);
  archiveScreen.renderOrder = 7;
  const archiveFramePoints = [];
  const frameWidth = 25.2;
  const frameHeight = 14.7;
  appendSegment(archiveFramePoints, new THREE.Vector3(-frameWidth/2,-frameHeight/2,0), new THREE.Vector3(frameWidth/2,-frameHeight/2,0));
  appendSegment(archiveFramePoints, new THREE.Vector3(frameWidth/2,-frameHeight/2,0), new THREE.Vector3(frameWidth/2,frameHeight/2,0));
  appendSegment(archiveFramePoints, new THREE.Vector3(frameWidth/2,frameHeight/2,0), new THREE.Vector3(-frameWidth/2,frameHeight/2,0));
  appendSegment(archiveFramePoints, new THREE.Vector3(-frameWidth/2,frameHeight/2,0), new THREE.Vector3(-frameWidth/2,-frameHeight/2,0));
  [-.45,-.15,.15,.45].forEach(offset =>
    appendSegment(archiveFramePoints, new THREE.Vector3(-frameWidth/2,offset,0), new THREE.Vector3(-frameWidth/2-.75,offset,0)));
  const archiveFrameMaterial = lineMaterial(0);
  archiveFrameMaterial.depthTest = false;
  archive.add(archiveScreen, lineSegments(archiveFramePoints, archiveFrameMaterial));
  chamber.add(archive);

  // Application signal route. Four gates resolve into one accepted packet.
  const application = new THREE.Group();
  const applicationMaterial = lineMaterial(0);
  const applicationDimMaterial = lineMaterial(0);
  const applicationBright = [];
  const applicationDim = [];
  for (let gate = 0; gate < 4; gate += 1) {
    const z = -28 + gate * 18;
    const ring = appendRing(applicationBright, new THREE.Vector3(0, 2, z), 8.2 - gate * .65, 8, "xy", Math.PI / 8);
    if (gate > 0) {
      ring.forEach((point, index) => {
        if (index % 2 === 0) appendSegment(applicationDim, new THREE.Vector3(point.x, point.y, z - 18), point);
      });
    }
  }
  const applicationLine = lineSegments(applicationBright, applicationMaterial);
  const applicationDimLine = lineSegments(applicationDim, applicationDimMaterial);
  application.add(applicationLine, applicationDimLine);
  const packet = edgedMesh(new THREE.OctahedronGeometry(1.5, 0), paleMaterial, .9);
  packet.position.z = -30;
  application.add(packet);
  chamber.add(application);

  // Supply bay outlines support the DOM garments without duplicating them in WebGL.
  const supply = new THREE.Group();
  const supplyMaterial = lineMaterial(0);
  const supplyLines = [];
  [-1, 1].forEach(side => {
    const x = side * 14;
    const corners = [
      new THREE.Vector3(x - 8, -6, 0), new THREE.Vector3(x + 8, -6, 0),
      new THREE.Vector3(x + 8, 13, 0), new THREE.Vector3(x - 8, 13, 0),
    ];
    corners.forEach((point, index) => appendSegment(supplyLines, point, corners[(index + 1) % 4]));
    appendSegment(supplyLines, new THREE.Vector3(x, -6, 0), new THREE.Vector3(x, 13, 0));
    appendSegment(supplyLines, new THREE.Vector3(x - 8, 3.5, 0), new THREE.Vector3(x + 8, 3.5, 0));
  });
  supply.add(lineSegments(supplyLines, supplyMaterial));
  chamber.add(supply);

  const cameraKeys = [
    {at:0, position:[0,1.6,29], target:[0,1,-2]},
    {at:.055, position:[-1.4,2.3,25], target:[0,1,-4]},
    {at:.105, position:[1.1,1.2,18], target:[0,1,-18]},
    {at:.155, position:[0,.2,-84], target:[0,0,-150]},
    {at:.205, position:[0,7,-166], target:[0,0,-210]},
    {at:.31, position:[-35,8,-180], target:[8,1,-210]},
    {at:.45, position:[-43,12,-221], target:[0,1,-210]},
    {at:.59, position:[-13,13,-252], target:[0,1,-210]},
    {at:.72, position:[34,9,-242], target:[0,1,-210]},
    {at:.84, position:[42,6,-199], target:[0,1,-210]},
    {at:1, position:[0,8,-164], target:[0,1,-210]},
  ];
  function cameraState(progress) {
    for (let index = 0; index < cameraKeys.length - 1; index += 1) {
      const start = cameraKeys[index];
      const end = cameraKeys[index + 1];
      if (progress <= end.at) {
        const amount = smooth((progress - start.at) / Math.max(.0001, end.at - start.at));
        return {
          position: start.position.map((value, axis) => mix(value, end.position[axis], amount)),
          target: start.target.map((value, axis) => mix(value, end.target[axis], amount)),
        };
      }
    }
    return cameraKeys.at(-1);
  }

  let selectedDivision = 0;
  let targetProgress = 0;
  let progress = 0;
  let raf = 0;
  let last = performance.now();
  let visible = true;
  let pointerX = 0;
  let pointerY = 0;
  let needsRender = true;
  let qualityFrames = 0;
  let qualityTime = 0;
  const cameraTarget = new THREE.Vector3();
  const darkTone = new THREE.Color(0xf1f1ed);
  const lightTone = new THREE.Color(0x101010);
  const signalTone = new THREE.Color();
  const backgroundTone = new THREE.Color();
  const darkBackground = new THREE.Color(0x050505);
  const lightBackground = new THREE.Color(0xe8e8e3);
  const shardDummy = new THREE.Object3D();
  const shardPosition = new THREE.Vector3();
  const shardScale = new THREE.Vector3();
  const shardQuaternion = new THREE.Quaternion();
  const homeQuaternion = new THREE.Quaternion();
  const tunnelQuaternion = new THREE.Quaternion();

  function setDivision(index) {
    selectedDivision = clamp(index, 0, 5);
    needsRender = true;
  }
  function setProgress(value) {
    targetProgress = clamp(value);
    needsRender = true;
  }
  function pointerMove(event) {
    pointerX = (event.clientX / innerWidth - .5) * 2;
    pointerY = (event.clientY / innerHeight - .5) * 2;
    needsRender = true;
  }

  function resize() {
    const width = Math.max(1, container.clientWidth || innerWidth);
    const height = Math.max(1, container.clientHeight || innerHeight);
    renderer.setPixelRatio(pixelRatio);
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    needsRender = true;
  }

  function render(now) {
    raf = requestAnimationFrame(render);
    const delta = Math.min(.05, Math.max(.001, (now - last) / 1000));
    last = now;
    if (!visible || document.hidden) return;
    const settling = Math.abs(targetProgress - progress) > .0001;
    if (reduced) progress = targetProgress;
    else progress += (targetProgress - progress) * (1 - Math.exp(-delta * 9.5));
    if (!settling && !needsRender && now % 4 > 1) return;
    needsRender = false;

    const state = cameraState(progress);
    const px = mobile ? state.position[0] * .72 : state.position[0] + pointerX * .55;
    const py = state.position[1] - (mobile ? 0 : pointerY * .32);
    camera.position.set(px, py, state.position[2]);
    cameraTarget.set(state.target[0] + (mobile ? 0 : pointerX * .16), state.target[1], state.target[2]);
    camera.lookAt(cameraTarget);
    const passageRoll = smooth((progress - .09) / .045) * (1 - smooth((progress - .19) / .035));
    camera.rotateZ(Math.sin(progress * Math.PI * 10) * (.007 + passageRoll * .032));

    const inversion = windowFade(progress, .568, .586, .716, .734);
    signalTone.copy(darkTone).lerp(lightTone, inversion);
    lineMaterials.forEach(material => material.color.copy(signalTone));
    backgroundTone.copy(darkBackground).lerp(lightBackground, inversion);
    scene.background.copy(backgroundTone);
    scene.fog.color.copy(backgroundTone);
    dustMaterial.color.copy(signalTone);
    surfaceMaterials.forEach(material => {
      const pale = material.userData.signalRole === "pale";
      const from = pale ? .91 : .035;
      const to = pale ? .025 : .78;
      material.color.setRGB(mix(from, to, inversion), mix(from, to, inversion), mix(pale ? .89 : .035, pale ? .025 : .76, inversion));
      material.emissiveIntensity = pale ? mix(.14, 0, inversion) : 0;
    });
    const homeHold = 1 - smooth((progress - .145) / .07);
    const mobilize = smooth((progress - .068) / .075);
    const passage = smooth((progress - .105) / .055);
    signalHome.visible = homeHold > .002 || progress < .205;
    typeMaterial.opacity = homeHold * mix(.11, .028, mobilize);
    signalType.position.z = -16 - mobilize * 21;
    signalType.scale.setScalar(mix(1, 1.34, passage));
    signalHalo.rotation.z = progress * .28;
    signalHalo.scale.setScalar(mix(1, 1.22, mobilize));
    haloMaterialA.opacity = homeHold * mix(.66, .16, mobilize);
    haloMaterialB.opacity = homeHold * mix(.17, .055, mobilize);
    homeGlow.material.opacity = homeHold * mix(.13, .035, mobilize);
    homeGlow.scale.setScalar(mix(28, 44, mobilize));
    homeLight.intensity = homeHold * mix(10, 3.5, mobilize);
    homeLight.position.x = Math.sin(now * .00034) * 13 * homeHold;
    homeLight.position.y = 2 + Math.cos(now * .00027) * 7 * homeHold;

    homeShards.forEach(shard => {
      const local = smooth(clamp((mobilize - shard.delay) / Math.max(.001, 1 - shard.delay)));
      shardPosition.copy(shard.homePosition).lerp(shard.tunnelPosition, local);
      shardScale.copy(shard.homeScale).lerp(shard.tunnelScale, local);
      homeQuaternion.setFromEuler(shard.homeRotation);
      tunnelQuaternion.setFromEuler(shard.tunnelRotation);
      shardQuaternion.copy(homeQuaternion).slerp(tunnelQuaternion, local);
      const flare = Math.sin(local * Math.PI) * (1.5 + Math.abs(shard.homePosition.x) * .035);
      shardPosition.x += shard.wing * flare;
      shardDummy.position.copy(shardPosition);
      shardDummy.quaternion.copy(shardQuaternion);
      shardDummy.scale.copy(shardScale);
      shardDummy.updateMatrix();
      const set = crystalMeshes[shard.type];
      set.surface.setMatrixAt(shard.slot, shardDummy.matrix);
      set.wire.setMatrixAt(shard.slot, shardDummy.matrix);
    });
    crystalMeshes.forEach(({surface,wire}) => {
      surface.instanceMatrix.needsUpdate = true;
      wire.instanceMatrix.needsUpdate = true;
      surface.material.opacity = mix(.94, .62, passage) * (1 - inversion * .72);
      wire.material.opacity = mix(.38, .14, passage) * (1 - inversion * .72);
    });

    homeLogoObject.visible = homeHold > .002;
    homeLogoObject.rotation.y = mix(0, Math.PI * .38, mobilize);
    homeLogoObject.rotation.x = Math.sin(progress * 18) * .018 * (1 - mobilize);
    homeLogoObject.position.z = -1 - mobilize * 38;
    homeLogoObject.scale.setScalar((mobile ? .72 : .92) * mix(1, .46, mobilize));
    homeLogoObject.userData.mesh.material.opacity = homeHold;
    homeLogoObject.userData.edges.material.opacity = homeHold;

    const chamberVisibility = smooth((progress - .165) / .065);
    chamber.visible = chamberVisibility > .002;
    const capitalOpacity = windowFade(progress, .15, .195, .345, .47);
    sectorCapital.visible = capitalOpacity > .002;
    sectorCapital.rotation.y = -.12 + smooth((progress - .16) / .29) * .58;
    sectorCapital.rotation.z = mix(-.025, .018, smooth((progress - .17) / .2));
    sectorCapital.position.y = mix(1.5, 5.5, smooth((progress - .15) / .1));
    capitalBrightMaterial.opacity = capitalOpacity * .78;
    capitalDimMaterial.opacity = capitalOpacity * .17;
    capitalSlabMaterial.opacity = capitalOpacity * .83;
    capitalSlabWireMaterial.opacity = capitalOpacity * .105;
    tissueMaterial.opacity = capitalOpacity * .78;
    tissueWireMaterial.opacity = capitalOpacity * .24;
    floorBrightMaterial.opacity = chamberVisibility * (.18 + .3 * smooth((progress - .15) / .3)) * (1 - capitalOpacity * .42);
    floorDimMaterial.opacity = chamberVisibility * .1 * (1 - capitalOpacity * .35);
    dustMaterial.opacity = chamberVisibility * mix(.19, .28, 1 - capitalOpacity);
    dust.rotation.y = progress * .08 + now * .000012;
    const tunnelWindow = smooth((progress - .085) / .025) * (1 - smooth((progress - .205) / .055));
    const exposurePulse = .5 + .5 * Math.sin(progress * 105);
    passageLight.intensity = tunnelWindow * mix(7, 13, exposurePulse);
    renderer.toneMappingExposure = 1.12 + tunnelWindow * mix(.08, .32, exposurePulse);
    tunnelBrightMaterial.opacity = tunnelWindow * mix(.62, .92, exposurePulse);
    tunnelDimMaterial.opacity = tunnelWindow * .17;
    shellDarkMaterial.opacity = tunnelWindow * .94;
    shellPaleMaterial.opacity = tunnelWindow * mix(.3, .72, exposurePulse);
    shellDarkMaterial.roughness = mix(.23, .11, passage);
    tunnel.visible = tunnelBrightMaterial.opacity > .002;

    const formation = formationAt(progress);
    monoliths.forEach((monolith, index) => {
      const angle = index / 6 * Math.PI * 2 + formation.rotation;
      const radius = formation.radius * (index % 2 ? formation.spread : 1);
      monolith.position.set(Math.cos(angle) * radius, -7.2 + 13 * formation.height, Math.sin(angle) * radius);
      monolith.scale.set(1, formation.height, 1);
      monolith.lookAt(0, monolith.position.y, 0);
      const divisionEmphasis = progress > .39 && progress < .62 && index === selectedDivision;
      const capitalRestraint = 1 - capitalOpacity * .58;
      monolith.userData.mesh.material.opacity = chamberVisibility * (divisionEmphasis ? .98 : .78) * capitalRestraint;
      monolith.userData.edges.material.opacity = chamberVisibility * (divisionEmphasis ? 1 : .34) * mix(.62, 1, capitalRestraint);
      const pulse = .5 + Math.sin(now * .0022 + index * 1.7) * .5;
      monolith.userData.slot.material.opacity = chamberVisibility * (divisionEmphasis ? .78 + pulse * .22 : .08 + pulse * .055);
      monolith.userData.slot.material.color.copy(signalTone);
    });

    // The return resolves into a symmetrical honor guard, leaving a clean,
    // uninterrupted center axis for the RiZe mark.
    if (progress > .855) {
      const returnAmount = smooth((progress - .855) / .11);
      const finalX = [-33, -22, -11, 11, 22, 33];
      const finalZ = [8, 3, -1, -1, 3, 8];
      monoliths.forEach((monolith, index) => {
        monolith.position.x = mix(monolith.position.x, finalX[index], returnAmount);
        monolith.position.z = mix(monolith.position.z, finalZ[index], returnAmount);
        monolith.lookAt(0, monolith.position.y, -10);
      });
    }
    const archiveOpacity = windowFade(progress, .235, .285, .405, .455);
    archive.visible = archiveOpacity > .002;
    archiveScreenMaterial.opacity = archiveOpacity * .76;
    archiveFrameMaterial.opacity = archiveOpacity * .72;
    archive.position.y = mix(-6, 3, smooth((progress - .235) / .06));
    archive.position.z = 9;
    archive.lookAt(camera.position.x, camera.position.y, camera.position.z - chamber.position.z);
    const archiveScale = mobile ? .72 : 1;
    archive.scale.setScalar(archiveScale);

    const applicationOpacity = windowFade(progress, .545, .59, .72, .77);
    application.visible = applicationOpacity > .002;
    applicationMaterial.opacity = applicationOpacity * .82;
    applicationDimMaterial.opacity = applicationOpacity * .18;
    application.rotation.y = -Math.PI / 2;
    application.position.x = mix(-9, 4, smooth((progress - .56) / .17));
    packet.position.z = mix(-30, 26, smooth((progress - .585) / .12));
    packet.rotation.x = progress * 18;
    packet.rotation.y = progress * 13;
    packet.userData.mesh.material.opacity = applicationOpacity;
    packet.userData.edges.material.opacity = applicationOpacity;

    const supplyOpacity = windowFade(progress, .695, .73, .855, .9);
    supply.visible = supplyOpacity > .002;
    supplyMaterial.opacity = supplyOpacity * .34;
    supply.rotation.y = Math.PI * .5;
    supply.position.x = mix(12, 0, smooth((progress - .70) / .08));

    const finalMix = smooth((progress - .855) / .145);
    const logoBase = chamberVisibility * (1 - archiveOpacity * .72) * (1 - applicationOpacity * (mobile ? .68 : .1));
    logoRoot.visible = logoBase > .002;
    const logoSpin = (progress < .82 ? progress * Math.PI * 1.5 : mix(progress * Math.PI * 1.5, Math.PI * 2, finalMix)) + now * .000035 * (1 - finalMix);
    logoRoot.rotation.y = logoSpin * (1 - capitalOpacity * .92);
    logoRoot.rotation.x = Math.sin(progress * Math.PI * 2) * .035 * (1 - finalMix) * (1 - capitalOpacity);
    logoRoot.scale.setScalar((mobile ? .62 : .82) * mix(1 + capitalOpacity * .34, 1.34, finalMix));
    logoRoot.userData.mesh.material.opacity = logoBase * (mobile ? 1 - capitalOpacity * .68 : 1);
    logoRoot.userData.edges.material.opacity = mix(.66, 1, finalMix) * logoBase;
    logoGlow.material.opacity = chamberVisibility * mix(.07 + capitalOpacity * .09, .24, finalMix);
    logoGlow.scale.setScalar(mix(20 + capitalOpacity * 6, 34, finalMix));
    heartLight.intensity = chamberVisibility * mix(2.8 + capitalOpacity * 2.2, 6.5, finalMix);

    renderer.render(scene, camera);
    if (!readyResolved) {
      readyResolved = true;
      resolveReady();
    }

    if (!reduced && pixelRatio > .73) {
      qualityFrames += 1;
      qualityTime += delta;
      if (qualityFrames >= 45) {
        const average = qualityTime / qualityFrames;
        if (average > .026) {
          pixelRatio = Math.max(.72, pixelRatio * .86);
          resize();
        }
        qualityFrames = 0;
        qualityTime = 0;
      }
    }
  }

  const visibilityObserver = new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting;
    needsRender = true;
  }, {rootMargin: "15% 0px"});
  visibilityObserver.observe(container);
  const onVisibility = () => {
    visible = !document.hidden;
    needsRender = true;
  };
  const pointerQuery = matchMedia("(hover:hover) and (pointer:fine)");
  if (pointerQuery.matches) addEventListener("pointermove", pointerMove, {passive: true});
  addEventListener("resize", resize, {passive: true});
  document.addEventListener("visibilitychange", onVisibility);
  canvas.addEventListener("webglcontextlost", event => {
    event.preventDefault();
    container.classList.add("is-static");
  }, {passive: false});
  canvas.addEventListener("webglcontextrestored", () => {
    container.classList.remove("is-static");
    resize();
  });
  resize();
  renderer.compile(scene, camera);
  raf = requestAnimationFrame(render);

  return {
    ready,
    setProgress,
    setDivision,
    destroy() {
      cancelAnimationFrame(raf);
      visibilityObserver.disconnect();
      removeEventListener("resize", resize);
      removeEventListener("pointermove", pointerMove);
      document.removeEventListener("visibilitychange", onVisibility);
      scene.traverse(object => {
        if (object.geometry) object.geometry.dispose();
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        materials.filter(Boolean).forEach(material => {
          if (material.map) material.map.dispose();
          material.dispose();
        });
      });
      renderer.dispose();
      canvas.remove();
    },
  };
}
